/**
 * User Coins Page (Phase 2: Coins)
 *
 * Shows the signed-in user's coin balance and the active SAR-per-coin rate,
 * plus a paginated transaction history with localized type labels and signed
 * amounts (green for credits, red for debits). Sign-in gated like the add-ons
 * storefront.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SignInPrompt from '../../components/common/SignInPrompt';
import { useAuth } from '../../hooks/useAuth';
import {
  getBalance,
  getHistory,
  type CoinTransaction,
  type CoinTxnType,
  type CoinHistoryMeta,
} from '../../services/coinService';
import { formatAmount, latnLocale } from '../../utils/numberFormat';

const PAGE_SIZE = 20;

const TXN_TYPES: CoinTxnType[] = [
  'PLAN_GRANT',
  'ADMIN_CREDIT',
  'ADMIN_DEBIT',
  'PURCHASE_SPEND',
  'DISCOUNT_REDEEM',
  'REFUND_CREDIT',
  'ADJUSTMENT',
];

export default function CoinsPage() {
  const { t, i18n } = useTranslation('coins');
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();

  const [balance, setBalance] = useState<number | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<CoinTransaction[]>([]);
  const [meta, setMeta] = useState<CoinHistoryMeta | null>(null);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<CoinTxnType | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const [balanceRes, historyRes] = await Promise.all([
        getBalance(),
        getHistory({
          page,
          pageSize: PAGE_SIZE,
          type: typeFilter || undefined,
        }),
      ]);
      setBalance(balanceRes.balance);
      setRate(balanceRes.rateSarPerCoin);
      setTransactions(historyRes.data);
      setMeta(historyRes.meta);
    } catch (err) {
      console.error('Failed to load coins:', err);
      setError(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [user, page, typeFilter, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-GB'), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const totalPages = meta?.totalPages ?? 1;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-3">
            {t('pageTitle')}
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto">{t('pageSubtitle')}</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {!user ? (
          <SignInPrompt message={t('signInToView')} />
        ) : isLoading && balance === null ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
          </div>
        ) : (
          <>
            {/* Balance card */}
            <div className="mb-8 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-copper-coin-line text-3xl text-[#004D40]"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('balance')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {formatAmount(balance ?? 0)}{' '}
                    <span className="text-base font-medium text-gray-500">
                      {t('balanceUnit')}
                    </span>
                  </p>
                  {rate !== null && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t('rateNote', { rate: formatAmount(rate) })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* History */}
            <section>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] dark:text-white">
                  {t('historyTitle')}
                </h2>
                <select
                  value={typeFilter}
                  onChange={(e) => {
                    setPage(1);
                    setTypeFilter(e.target.value as CoinTxnType | '');
                  }}
                  className="px-4 py-2 rounded-xl border dark:bg-gray-800 dark:border-gray-700 dark:text-white text-sm"
                >
                  <option value="">{t('filterAllTypes')}</option>
                  {TXN_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`type.${type}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('colDate')}
                        </th>
                        <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('colType')}
                        </th>
                        <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('colReason')}
                        </th>
                        <th className="px-4 py-3 text-end text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('colAmount')}
                        </th>
                        <th className="px-4 py-3 text-end text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('colBalance')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {transactions.map((txn) => {
                        const isCredit = txn.amount >= 0;
                        return (
                          <tr
                            key={txn.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          >
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              {formatDate(txn.createdAt)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {t(`type.${txn.type}`)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {txn.reason || '-'}
                            </td>
                            <td
                              className={`px-4 py-3 text-end text-sm font-semibold whitespace-nowrap ${
                                isCredit
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              {isCredit ? '＋' : '－'}
                              {formatAmount(Math.abs(txn.amount))}
                            </td>
                            <td className="px-4 py-3 text-end text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                              {formatAmount(txn.balanceAfter)}
                            </td>
                          </tr>
                        );
                      })}
                      {transactions.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-8 text-center text-gray-500"
                          >
                            {t('historyEmpty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-4 mt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || isLoading}
                    className="px-4 py-2 rounded-lg border dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('prev')}
                  </button>
                  <span className="text-sm text-gray-500">
                    {t('pageOf', { page, totalPages })}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || isLoading}
                    className="px-4 py-2 rounded-lg border dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('next')}
                  </button>
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
